import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import CustomField from "components/form/CustomField";
import MultiSelect from "components/common/MultiSelect";
import Switch from "components/common/Switch";

import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import { getAddonValidationSchema } from "utils/validations";
import { handleSubmitForm } from "helpers/apiUtils";
import {
  addAddon,
  getAddonDetails,
  updateAddon,
} from "store/slices/membershipSlice";
import { PLAN_OPTIONS } from "utils/constants";
import { usePermission } from "hooks/usePermission";
import NoPermission from "components/common/NoPermission";
import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import PageHeader from "components/common/PageHeader";

const AddEditAddon = ({ mode }) => {
  const { t } = useTranslation();
  const id = useQueryParam("id");
  const isEdit = mode === "edit";
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canEdit, canAdd } = canAction("membership_addon");

  const { isLoading, loading, addonDetails } = useSelector(
    (state) => state.Membership,
  );

  const [initialValues, setInitialValues] = useState({
    title: {
      en: "",
      ar: "",
    },
    description: {
      en: "",
      ar: "",
    },
    price: null,
    currency: "SAR",
    allowedFor: [],
    isUnlimited: true,
    maxUses: 0,
  });

  useEffect(() => {
    if (isEdit && id && canEdit) {
      dispatch(getAddonDetails(id));
    }
  }, [isEdit, id, dispatch, canEdit]);

  useEffect(() => {
    if (isEdit) {
      const { title, price, description, maxUses, isUnlimited, allowedFor } =
        addonDetails || {};
      const addon = {
        title,
        description,
        price,
        currency: "SAR",
        allowedFor,
        isUnlimited,
        maxUses,
      };

      setInitialValues(addon);
    }
  }, [isEdit, addonDetails]);

  const handleSubmit = (values) => {
    const payload = {
      title: {
        en: values.title.en,
        ar: values.title.ar,
      },
      description: {
        en: values.description.en,
        ar: values.description.ar,
      },
      price: values.price,
      currency: values.currency,
      allowedFor: values.allowedFor,
      isUnlimited: values.isUnlimited,
      maxUses: values.maxUses,
    };

    if (isEdit) {
      const updatedPayload = {
        ...payload,
        id,
      };
      handleSubmitForm({
        dispatch,
        action: updateAddon,
        payload: updatedPayload,
        onSuccess: () => navigate(-1),
      });
    } else {
      handleSubmitForm({
        dispatch,
        action: addAddon,
        payload,
        onSuccess: () => navigate(-1),
      });
    }
  };

  if (!canEdit && isEdit) return <NoPermission />;
  if (!canAdd && !isEdit) return <NoPermission />;

  if (isEdit && loading) return <AddEditLoader title={t("Edit Addon")} />;

  return (
    <div className="page-content">
      <PageHeader
        isBackBtn={true}
        title={isEdit ? t("Edit Addon") : t("Add Addon")}
      />
      <h3 className="page_sub_title">{t("Manage addon configurations")}</h3>

      <div className="common_card">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={getAddonValidationSchema(t, isEdit)}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title (English)")}
                      name="title.en"
                      placeholder={t("Enter Title (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="title.ar"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title (Arabic)")}
                      name="title.ar"
                      placeholder={t("Enter Title (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="title.en"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Price")}
                      name="price"
                      placeholder={t("Enter Price")}
                      type="number"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <MultiSelect
                      label={t("Allowed For")}
                      required
                      name="allowedFor"
                      value={values?.allowedFor || []}
                      options={PLAN_OPTIONS}
                      placeholder={t("Select Plan Types")}
                      onChange={(val) => setFieldValue("allowedFor", val)}
                      className={
                        errors?.allowedFor && touched?.allowedFor
                          ? "is-invalid"
                          : ""
                      }
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      required
                      label={t("Description (English)")}
                      name="description.en"
                      placeholder={t("Enter Description (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="description.ar"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      required
                      label={t("Description (Arabic)")}
                      name="description.ar"
                      placeholder={t("Enter Description (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="description.en"
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <Switch
                      label={t("Unlimited")}
                      checked={values.isUnlimited}
                      infoText={t(
                        "When enabled, customers can use this add-on an unlimited number of times.",
                      )}
                      handleOnChange={(e) =>
                        setFieldValue("isUnlimited", e.value)
                      }
                    />
                  </div>
                </Col>

                {!values.isUnlimited && (
                  <Col lg={6}>
                    <div className="form_field">
                      <CustomField
                        label={t("Max Uses")}
                        infoText={t(
                          "Sets how many times a customer can use this add-on when unlimited usage is disabled.",
                        )}
                        name="maxUses"
                        placeholder="0"
                        type="number"
                      />
                    </div>
                  </Col>
                )}

                <Col lg={12} className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>

                  <button
                    type="submit"
                    className="main_btn"
                    disabled={isLoading}
                  >
                    {isEdit ? t("Save Changes") : t("Add Addon")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddEditAddon;
